/**
 * /tracker — completions by agency (Scene 11, the agency's seat).
 *
 * The closing beat of the demo: the instructor awards, and this page — on the
 * projector — updates. It polls, for the same reason the roster does: the
 * moment it exists for is watching a number change, and nobody should have to
 * reload to see it.
 *
 * Who appears is RLS's decision (an agency admin sees their own people; the
 * hub sees everyone). This page groups and sums, and says plainly that it
 * reports what has been EARNED — what is DUE arrives with the course catalog.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { withLane } from '../lib/lane.js';
import LanePicker from '../components/LanePicker.jsx';

const DATE = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'America/New_York' });
const when = (iso) => (iso ? DATE.format(new Date(iso)) : '—');
const hours = (n) => `${Number(n).toFixed(Number(n) % 1 === 0 ? 0 : 2)} h`;

const STANDING = {
  expired: { label: 'Expired', cls: 'badge--warn' },
  due_soon: { label: 'Due soon', cls: 'badge--due' },
  current: { label: 'Current', cls: 'badge--ok' },
  no_expiry: { label: '—', cls: '' },
};

function Standing({ value }) {
  const s = STANDING[value] ?? STANDING.no_expiry;
  if (value === 'no_expiry') return <span className="muted">—</span>;
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

export function TrackerPage() {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await apiFetch(withLane('/api/v1/tracker'));
      setData(d);
      setError(null);
    } catch (err) {
      setError(err);
      if (data === undefined) setData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [load]);

  const toggle = (id) =>
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const q = filter.trim().toLowerCase();
  // Filtering re-sums the agency line, so "1 person · 15 completions" can
  // never appear.
  const agencies = (data?.agencies ?? [])
    .map((g) => {
      const people = q
        ? g.people.filter(
            (p) =>
              p.full_name.toLowerCase().includes(q) ||
              (p.external_id ?? '').includes(q) ||
              g.agency.toLowerCase().includes(q)
          )
        : g.people;
      return {
        ...g,
        people,
        completions: people.reduce((n, p) => n + p.completions, 0),
        hours: Math.round(people.reduce((n, p) => n + p.hours, 0) * 100) / 100,
        expired: people.filter((p) => p.standing === 'expired').length,
        due_soon: people.filter((p) => p.standing === 'due_soon').length,
      };
    })
    .filter((g) => g.people.length > 0);

  return (
    <>
      <div className="page-head">
        <h1>Tracker</h1>
        {data && <LanePicker tenant={data.tenant} tenants={data.tenants} onChange={() => load()} />}
      </div>
      <ErrorBanner error={error} />

      <p className="muted">
        Completions by agency, from the certified record — a person affiliated with two agencies appears
        under both, with what they earned under each — and where each person stands on the{' '}
        <Link to="/catalog">catalog</Link> courses they have completed. A card is good for its catalog
        term from the day of the class; <strong>Due soon</strong> means inside {data?.due_soon_days ?? 90} days.
        Updates on its own as instructors award.
      </p>

      {data === undefined && (
        <p className="muted" aria-busy="true">
          Loading…
        </p>
      )}

      {data && (
        <>
          <div className="tracker__totals" aria-live="polite">
            <div className="card tracker__stat">
              <span className="tracker__stat-number">{data.totals.people}</span>
              <span className="muted">people with a record</span>
            </div>
            <div className="card tracker__stat">
              <span className="tracker__stat-number">{data.totals.completions}</span>
              <span className="muted">completions</span>
            </div>
            <div className="card tracker__stat">
              <span className="tracker__stat-number">{hours(data.totals.hours)}</span>
              <span className="muted">earned</span>
            </div>
            <div className="card tracker__stat">
              <span className="tracker__stat-number">{data.totals.expired}</span>
              <span className="muted">people with something expired</span>
            </div>
            <div className="card tracker__stat">
              <span className="tracker__stat-number">{data.totals.due_soon}</span>
              <span className="muted">people due soon</span>
            </div>
          </div>

          {data.courses?.length > 0 && (
            <section className="panel" aria-labelledby="by-course-heading">
              <h2 id="by-course-heading">By course</h2>
              <div className="schedule__scroll">
                <table className="schedule__table tracker__courses">
                  <thead>
                    <tr>
                      <th scope="col">Course</th>
                      <th scope="col">Current</th>
                      <th scope="col">Due soon</th>
                      <th scope="col">Expired</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.courses.map((c) => (
                      <tr key={c.code}>
                        <td>
                          <strong>{c.code}</strong> <span className="muted">{c.name}</span>
                        </td>
                        <td>{c.current}</td>
                        <td>{c.due_soon}</td>
                        <td>{c.expired}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <div className="field tracker__filter">
            <label htmlFor="tracker-filter">Find a person, ID or agency</label>
            <input
              id="tracker-filter"
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Riley, 1216426, Fairview…"
            />
          </div>

          {agencies.length === 0 && (
            <p className="muted">
              {q ? 'Nobody matches that.' : 'No completions have been awarded yet.'}
            </p>
          )}

          {agencies.map((g) => (
            <section key={g.agency} className="panel tracker__agency" aria-labelledby={`ag-${g.agency}`}>
              <h2 id={`ag-${g.agency}`}>
                {g.agency}{' '}
                <span className="muted tracker__agency-sum">
                  · {g.people.length} {g.people.length === 1 ? 'person' : 'people'} · {g.completions}{' '}
                  {g.completions === 1 ? 'completion' : 'completions'} · {hours(g.hours)}
                  {g.expired > 0 && ` · ${g.expired} expired`}
                  {g.due_soon > 0 && ` · ${g.due_soon} due soon`}
                </span>
              </h2>
              <div className="schedule__scroll">
                <table className="schedule__table tracker__table">
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">A-B Tech ID</th>
                      <th scope="col">Role</th>
                      <th scope="col">Completed</th>
                      <th scope="col">Hours</th>
                      <th scope="col">Standing</th>
                      <th scope="col">Most recent</th>
                      <th scope="col">
                        <span className="visually-hidden">Details</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.people.map((p) => (
                      <PersonRows key={`${p.roster_id}|${p.agency}`} person={p} open={open.has(p.roster_id)} onToggle={() => toggle(p.roster_id)} />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </>
      )}
    </>
  );
}

function PersonRows({ person: p, open, onToggle }) {
  return (
    <>
      <tr>
        <td>
          {p.full_name}
          {p.other_agencies?.length > 0 && (
            <>
              <br />
              <span className="muted">also at {p.other_agencies.join(', ')}</span>
            </>
          )}
        </td>
        <td>{p.external_id ?? <span className="muted">unconfirmed</span>}</td>
        <td>{p.role_title ?? <span className="muted">—</span>}</td>
        <td>
          {p.completions}
          {p.incomplete > 0 && <span className="muted"> ({p.incomplete} incomplete)</span>}
        </td>
        <td>{hours(p.hours)}</td>
        <td>
          <Standing value={p.standing} />
          {p.courses?.length > 0 && p.standing !== 'no_expiry' && (
            <span className="muted">
              {' '}
              {p.expired > 0 && `${p.expired} expired`}
              {p.expired > 0 && p.due_soon > 0 && ', '}
              {p.due_soon > 0 && `${p.due_soon} due soon`}
            </span>
          )}
        </td>
        <td>
          {p.last_completed_at ? (
            <>
              {when(p.last_completed_at)}
              {p.last_section && <span className="muted"> · {p.last_section}</span>}
            </>
          ) : (
            <span className="muted">—</span>
          )}
        </td>
        <td>
          <button type="button" className="btn btn--quiet" onClick={onToggle} aria-expanded={open}>
            {open ? 'Hide' : 'Details'}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="tracker__detail">
          <td colSpan={8}>
            {p.courses?.length > 0 && (
              <ul className="tracker__records tracker__standing">
                {p.courses.map((c) => (
                  <li key={c.code}>
                    <Standing value={c.standing} /> <strong>{c.code}</strong> {c.name} · completed {when(c.completed_at)}
                    {c.expires_at ? ` · good until ${when(c.expires_at)}` : ' · does not expire'}
                  </li>
                ))}
              </ul>
            )}
            <ul className="tracker__records">
              {p.records.map((r) => (
                <li key={r.id}>
                  <span className={`badge ${r.outcome === 'complete' ? 'badge--ok' : 'badge--warn'}`}>
                    {r.outcome === 'complete' ? 'Complete' : 'Incomplete'}
                  </span>{' '}
                  {r.course && <strong>{r.course} · </strong>}
                  {r.title}
                  {r.section && <span className="muted"> · {r.section}</span>} · {when(r.date)} ·{' '}
                  {hours(r.ce_hours)}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

export default TrackerPage;
